import '@shopify/polaris/build/esm/styles.css';
import enPolarisTranslations from '@shopify/polaris/locales/en.json';
import { AppProvider } from '@shopify/polaris';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, SessionExpiredError } from './lib/api.js';
import type { SessionStatus } from './lib/types.js';
import { ReconnectBanner } from './components/ReconnectBanner.js';
import { Dashboard } from './pages/Dashboard.js';
import { ProductDetail } from './pages/ProductDetail.js';
import { BulkFix } from './pages/BulkFix.js';
import { Settings } from './pages/Settings.js';
import { Plans } from './pages/Plans.js';
import { BadgeSettings } from './pages/BadgeSettings.js';
import i18n from './i18n.js';
import { LanguagePicker, hasPickedLanguage } from './components/LanguagePicker.js';

/**
 * App shell. On mount we call GET /api/session-status. If it 401s the
 * merchant needs to re-auth — show a ReconnectBanner + top-level redirect.
 * Otherwise the session is live and we render routes.
 *
 * See docs/CONTEXT.md §4 "Auth" for the rationale on JWT-only auth and
 * why we must never do server-side page-load redirects.
 */
export function App(): JSX.Element {
  const [ready, setReady] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [shop, setShop] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [languageReady, setLanguageReady] = useState(hasPickedLanguage);
  const { i18n: i18nInstance } = useTranslation();

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const status = await api.get<SessionStatus>('/api/session-status', ac.signal);
        setShop(status.shop);
        setReady(true);
      } catch (e) {
        if (ac.signal.aborted) return;
        if (e instanceof SessionExpiredError) {
          const params = new URLSearchParams(window.location.search);
          setShop(params.get('shop'));
          setNeedsReconnect(true);
          return;
        }
        // Surface unexpected errors instead of rendering a blank screen — the
        // most common cause in dev is a missing/empty SHOPIFY_API_KEY meta tag
        // (App Bridge CDN fails to attach `window.shopify`, so idToken()
        // throws before the fetch even fires).
        const msg = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error('App boot failed:', e);
        setBootError(msg);
      }
    })();
    return () => ac.abort();
  }, []);

  // Sync i18n language from server settings (fires once after session loads).
  useEffect(() => {
    if (!ready) return;
    const ac = new AbortController();
    (async () => {
      try {
        const { settings } = await api.get<{ settings: { language: string } }>(
          '/api/settings',
          ac.signal,
        );
        if (settings.language && settings.language !== i18nInstance.language) {
          void i18n.changeLanguage(settings.language);
        }
      } catch {
        // Non-critical; keep the browser-detected language.
      }
    })();
    return () => ac.abort();
  }, [ready, i18nInstance.language]);

  // Shopify only adds a `host` param when the app is opened embedded inside
  // admin. Its absence + an App Bridge failure means the app was opened
  // standalone (e.g. hitting the Railway URL directly) — that's expected, not
  // a misconfiguration, so we show guidance instead of a scary error.
  const openedOutsideAdmin =
    bootError === 'app_bridge_not_loaded' &&
    !new URLSearchParams(window.location.search).get('host');

  return (
    <AppProvider i18n={enPolarisTranslations}>
      {needsReconnect ? (
        <div style={{ padding: 16 }}>
          <ReconnectBanner shop={shop} />
        </div>
      ) : openedOutsideAdmin ? (
        <div
          style={{
            padding: 24,
            maxWidth: 480,
            margin: '48px auto',
            textAlign: 'center',
            fontFamily: 'system-ui',
            lineHeight: 1.6,
          }}
        >
          <h2>Open this app from your Shopify admin</h2>
          <p style={{ color: '#555' }}>
            AI Compliance Scanner runs inside Shopify. Open it from your store
            admin under <strong>Apps → AI Compliance Scanner</strong>. Opening
            this URL directly won’t work — Shopify doesn’t provide the store
            context an embedded app needs here.
          </p>
        </div>
      ) : bootError ? (
        <div style={{ padding: 16, fontFamily: 'monospace' }}>
          <h2>App failed to load</h2>
          <p>{bootError}</p>
          <p style={{ color: '#666', fontSize: 12 }}>
            Check the browser console for details. If this says{' '}
            <code>app_bridge_not_loaded</code> while embedded in Shopify admin,
            the <code>shopify-api-key</code> meta tag is empty — ensure{' '}
            <code>SHOPIFY_API_KEY</code> is set in the server environment (dev:{' '}
            <code>.env</code> + restart; prod: the host’s env vars).
          </p>
        </div>
      ) : ready && !languageReady ? (
        <LanguagePicker onDone={() => setLanguageReady(true)} />
      ) : ready ? (
        <BrowserRouter>
          {/*
            Shopify admin left-rail nav. Rendered into the Shopify admin
            chrome by App Bridge v4 — not our iframe — so it's the canonical
            way a merchant reaches Settings / Badge / Plans from anywhere in
            the app. The `rel="home"` anchor is required; Shopify uses it
            to label the app's root. Child `<a>` clicks are intercepted by
            App Bridge and navigate inside the iframe, which BrowserRouter
            picks up via popstate.
          */}
          <ui-nav-menu>
            <a href="/" rel="home">Dashboard</a>
            <a href="/settings">Settings</a>
            <a href="/badge">Badge</a>
            <a href="/plans">Plans</a>
          </ui-nav-menu>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/issue/:issueId" element={<ProductDetail />} />
            <Route path="/bulk-fix/:scanId" element={<BulkFix />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/badge" element={<BadgeSettings />} />
          </Routes>
        </BrowserRouter>
      ) : null}
    </AppProvider>
  );
}
